import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { AddPaymentMethodModal as AddPaymentMethodModalComponent } from 'components/add-payment-method-modal';
import useDialogModal from 'hooks/use-dialog-modal';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { usePaymentContext } from 'state/payment';
import { toast } from 'utils/toast';

import { useFeatureScanAndPay } from './hooks/use-feature-scan-and-pay';
import { useLoyaltyScanAndPay } from './hooks/use-loyalty-scan-and-pay';
import { THScanAndPayView } from './th-scan-and-pay.view';
import { IScanAndPayTextContent, ScanAndPayViews } from './types';
import { getSortedValidPaymentMethods, hasValidScanAndPayPaymentMethodOption } from './utils';

export const THScanAndPayContainer = () => {
  const enableTHScanAndPay = useFlag(LaunchDarklyFlag.ENABLE_TH_SCAN_AND_PAY);
  const { formatMessage } = useIntl();
  const { paymentMethods, loading: paymentLoading, getPrepaidPaymentMethod } = usePaymentContext();
  const { scanAndPayContent, loading: scanAndPayContentLoading } = useFeatureScanAndPay();
  const { user } = useAuthContext();

  const {
    userScanAndPayEnabled,
    updateScanAndPayStatus,
    loading: userScanAndPayStatusLoading,
  } = useLoyaltyScanAndPay();

  const timCard = getPrepaidPaymentMethod();

  const isLoading = useMemo(
    () => scanAndPayContentLoading || paymentLoading || userScanAndPayStatusLoading,
    [scanAndPayContentLoading, paymentLoading, userScanAndPayStatusLoading]
  );

  const [AddPaymentMethodModal, openAddPaymentMethodModal, , onDismiss] = useDialogModal<
    {},
    { onCardAdded: () => void; onDismiss: () => void }
  >({
    Component: AddPaymentMethodModalComponent,
  });

  const scanAndPayTextContent: IScanAndPayTextContent = {
    header: scanAndPayContent?.header?.locale,
    inactiveStateBody: scanAndPayContent?.inactiveStateText?.locale,
    noPaymentMethodStateBody: scanAndPayContent?.addNewPaymentMethodStateText?.locale,
  };

  const currentView = useMemo(() => {
    if (userScanAndPayEnabled) {
      return ScanAndPayViews.ACTIVE;
    }
    if (hasValidScanAndPayPaymentMethodOption(paymentMethods)) {
      return ScanAndPayViews.INACTIVE;
    }
    return ScanAndPayViews.NO_PAYMENT_METHOD;
  }, [paymentMethods, userScanAndPayEnabled]);

  if (!enableTHScanAndPay) {
    return null;
  }

  const selectedPaymentMethodId =
    user?.details.defaultScanAndPayAccountIdentifier ?? timCard?.accountIdentifier ?? '';

  const { selectedPaymentMethod, nonSelectedPaymentMethods } = getSortedValidPaymentMethods({
    paymentMethods,
    selectedPaymentMethodId,
  });

  return (
    <>
      <THScanAndPayView
        currentView={currentView}
        isLoading={isLoading}
        scanAndPayTextContent={scanAndPayTextContent}
        nonSelectedPaymentMethods={nonSelectedPaymentMethods}
        selectedPaymentMethod={selectedPaymentMethod}
        toggleScanAndPay={() => updateScanAndPayStatus(!userScanAndPayEnabled)}
        addPaymentMethodButtonText={formatMessage({ id: 'addPaymentMethod' })}
        openAddPaymentMethodModal={openAddPaymentMethodModal}
      />
      <AddPaymentMethodModal
        onCardAdded={() => toast.success(formatMessage({ id: 'paymentMethodAdded' }))}
        onDismiss={onDismiss}
      />
    </>
  );
};
